import { useEffect, useState } from 'react'
import { VehicleRow, VEHICLE_ROW_COLS_MARCHE, VEHICLE_ROW_COLS_MARCHE_LQ, VEHICLE_ROW_COLS_STOCK } from './VehicleRow'

const HEADER_BASE = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
  padding: '8px 10px',
  borderBottom: '1px solid var(--border)',
  userSelect: 'none',
  minWidth: 0,
  whiteSpace: 'nowrap',
}

function SortChevron({ active, desc }) {
  if (!active) return <span style={{ opacity: 0.25, fontSize: 10 }}>↕</span>
  return <span style={{ fontSize: 10, opacity: 0.85 }}>{desc ? '↓' : '↑'}</span>
}

function HeaderCell({ children, textAlign = 'left', sortable, sortColumn, sortDesc, onSort, colKey }) {
  const active = sortColumn === colKey
  if (sortable && onSort) {
    return (
      <button
        type="button"
        onClick={() => onSort(colKey)}
        style={{
          ...HEADER_BASE,
          background: 'transparent',
          border: 'none',
          borderBottom: '1px solid var(--border)',
          width: '100%',
          cursor: 'pointer',
          textAlign,
          fontFamily: 'inherit',
          display: 'flex',
          alignItems: 'center',
          justifyContent: textAlign === 'right' ? 'flex-end' : 'flex-start',
          gap: 6,
          color: active ? 'var(--text-secondary)' : 'var(--text-tertiary)',
        }}
      >
        {children}
        <SortChevron active={active} desc={sortDesc} />
      </button>
    )
  }
  return (
    <div
      style={{
        ...HEADER_BASE,
        textAlign,
        display: 'flex',
        alignItems: 'center',
        justifyContent: textAlign === 'right' ? 'flex-end' : 'flex-start',
      }}
    >
      {children}
    </div>
  )
}

export function VehicleRowList({
  variant = 'stock',
  rowHeightPx = 72,
  sortColumn,
  sortDesc = true,
  onSort,
  filterSlot,
  densitySlot,
  rows = [],
  showActionColumn = true,
  showMarcheDelay = false,
  selectable = false,
  selectedRowKeys = [],
  onToggleRowSelection,
  onToggleAllSelection,
  /** Si false, l’en-tête de colonnes défile avec le contenu (ex. Mon Stock). */
  stickyHeader = true,
  /** Carte + en-tête renforcés (Mon Stock — positionnement & concurrence). */
  visualStyle = 'default',
}) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(max-width: 640px)')
    const onChange = (e) => setIsMobile(e.matches)
    setIsMobile(mq.matches)
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }
    mq.addListener(onChange)
    return () => mq.removeListener(onChange)
  }, [])

  const baseCols = showActionColumn
    ? VEHICLE_ROW_COLS_STOCK
    : showMarcheDelay
      ? VEHICLE_ROW_COLS_MARCHE_LQ
      : VEHICLE_ROW_COLS_MARCHE
  const gridCols = selectable ? `26px ${baseCols}` : baseCols
  const selectedSet = new Set(selectedRowKeys)
  const allSelected = rows.length > 0 && rows.every((r) => selectedSet.has(r.rowKey))
  const h3 =
    variant === 'vendu'
      ? 'Date vente'
      : variant === 'nouvelle'
        ? 'En ligne'
        : variant === 'baisse'
          ? 'Montant baisse'
          : variant === 'opportunite'
            ? 'Écart cotation'
            : variant === 'concurrence'
              ? 'Concurrents'
              : 'Jours stock'

  const stockPanel = visualStyle === 'stockPanel'
  const headerBg = stockPanel
    ? 'linear-gradient(180deg, rgba(0,30,80,0.24) 0%, rgba(22,26,32,0.98) 100%)'
    : 'var(--surface, #161a1f)'

  /** Sur mobile le tableau déborde : scroll horizontal ; sticky désactivé (overflow casse le sticky). */
  const tableMinWidthPx = showMarcheDelay ? 780 : showActionColumn ? 640 : 560
  const headerRow = (
    <div
      style={{
        position: stickyHeader && !isMobile ? 'sticky' : 'static',
        top: stickyHeader && !isMobile ? 0 : undefined,
        zIndex: stickyHeader && !isMobile ? 2 : undefined,
        display: 'grid',
        gridTemplateColumns: gridCols,
        columnGap: isMobile ? 6 : 10,
        alignItems: 'center',
        padding: stockPanel ? '8px 12px 10px' : isMobile ? '0 8px 4px' : '0 10px 4px',
        background: headerBg,
        borderBottom: stockPanel ? '1px solid rgba(148,163,184,0.18)' : '1px solid var(--border)',
        boxShadow: stockPanel ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : undefined,
      }}
    >
      {selectable ? (
        <div style={{ ...HEADER_BASE, paddingLeft: 0, display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => onToggleAllSelection?.()}
            aria-label="Sélectionner toutes les lignes"
          />
        </div>
      ) : null}
      <div style={{ ...HEADER_BASE, paddingLeft: 0 }} aria-hidden />
      <HeaderCell sortable={!!onSort} sortColumn={sortColumn} sortDesc={sortDesc} onSort={onSort} colKey="model">
        Modèle
      </HeaderCell>
      <HeaderCell sortable={!!onSort} sortColumn={sortColumn} sortDesc={sortDesc} onSort={onSort} colKey="jours">
        {h3}
      </HeaderCell>
      <HeaderCell sortable={!!onSort} sortColumn={sortColumn} sortDesc={sortDesc} onSort={onSort} colKey="prix">
        Prix
      </HeaderCell>
      <HeaderCell sortable={!!onSort} sortColumn={sortColumn} sortDesc={sortDesc} onSort={onSort} colKey="ecart">
        Position
      </HeaderCell>
      {showMarcheDelay ? (
        <HeaderCell sortable={!!onSort} sortColumn={sortColumn} sortDesc={sortDesc} onSort={onSort} colKey="delai" textAlign="right">
          Délai moyen de vente
        </HeaderCell>
      ) : null}
      {showActionColumn ? (
        <HeaderCell textAlign="right" sortable={false}>
          Action
        </HeaderCell>
      ) : null}
    </div>
  )

  const listChrome = (
    <>
      {isMobile ? (
        <div
          style={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
            marginBottom: 4,
          }}
        >
          <div style={{ minWidth: tableMinWidthPx }}>
            {headerRow}
            <div style={{ padding: stockPanel ? '8px 8px 10px' : '0 8px' }}>
              {rows.map((r) => {
                const { _sort: _sk, ...row } = r
                return (
                  <VehicleRow
                    key={r.rowKey}
                    rowKey={r.rowKey}
                    rowHeightPx={rowHeightPx}
                    gridTemplateColumns={gridCols}
                    mobile={isMobile}
                    showAction={showActionColumn}
                    showMarcheDelay={showMarcheDelay}
                    daysCellSingleLine={false}
                    visualStyle={visualStyle}
                    selectionControl={
                      selectable ? (
                        <input
                          type="checkbox"
                          checked={selectedSet.has(r.rowKey)}
                          onChange={(e) => {
                            e.stopPropagation()
                            onToggleRowSelection?.(r.rowKey)
                          }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Sélectionner ${r.titleLine || r.rowKey}`}
                        />
                      ) : null
                    }
                    {...row}
                  />
                )
              })}
            </div>
          </div>
        </div>
      ) : (
        <>
          {headerRow}
          <div style={{ padding: stockPanel ? '8px 8px 10px' : '0 10px' }}>
            {rows.map((r) => {
          const { _sort: _sk, ...row } = r
          return (
            <VehicleRow
              key={r.rowKey}
              rowKey={r.rowKey}
              rowHeightPx={rowHeightPx}
              gridTemplateColumns={gridCols}
              mobile={isMobile}
              showAction={showActionColumn}
              showMarcheDelay={showMarcheDelay}
              daysCellSingleLine={false}
              visualStyle={visualStyle}
              selectionControl={
                selectable ? (
                  <input
                    type="checkbox"
                    checked={selectedSet.has(r.rowKey)}
                    onChange={(e) => {
                      e.stopPropagation()
                      onToggleRowSelection?.(r.rowKey)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Sélectionner ${r.titleLine || r.rowKey}`}
                  />
                ) : null
              }
              {...row}
            />
          )
        })}
          </div>
        </>
      )}
    </>
  )

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      {(filterSlot || densitySlot) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 16,
            marginBottom: 12,
          }}
        >
          {filterSlot}
          {densitySlot}
        </div>
      )}
      {stockPanel ? (
        <div
          style={{
            border: '1px solid rgba(148,163,184,0.14)',
            borderRadius: 6,
            overflow: 'hidden',
            background: 'rgba(15,23,42,0.42)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
          }}
        >
          {listChrome}
        </div>
      ) : (
        listChrome
      )}
    </div>
  )
}
